import { useMemo } from "react";

import { SlotMapConfiguration } from "src/gen/schema/models/models";
import { isBitSet } from "src/utils/bitmapUtils";

interface GpuSlotState {
  /** Set of all GPU slot numbers from the configuration */
  gpuSlotNumbers: ReadonlySet<number>;
  /** Set of GPU slots that don't have an active GPU installed */
  inactiveGpuSlots: ReadonlySet<number>;
  /** Map from NIC slot number to the GPU slots it serves */
  nicToGpuSlotsMap: ReadonlyMap<number, ReadonlySet<number>>;
}

/**
 * Custom hook to manage GPU slot state derived from the slot map configuration.
 *
 * This hook:
 * 1. Extracts GPU slot numbers from the configuration
 * 2. Determines which GPU slots are inactive based on the activeGpus bitmap
 * 3. Creates a mapping from NIC slots to their associated GPU slots
 *
 * @param gpuNicSlotMap - Configuration mapping GPU slots to NIC slots
 * @param activeGpus - Bitmap where bit N represents if GPU N+1 is active (1-indexed GPUs)
 * @returns GPU slot state including slot numbers, inactive slots, and NIC-to-GPU mapping
 */
export const useGpuSlotState = (
  gpuNicSlotMap: SlotMapConfiguration[],
  activeGpus: number
): GpuSlotState => {
  // Get the set of GPU slot numbers from gpuNicSlotMap
  const gpuSlotNumbers = useMemo(
    () => new Set(gpuNicSlotMap.map((config) => config.gpuSlot)),
    [gpuNicSlotMap]
  );

  // Create a set of GPU slots that don't have an active GPU
  const inactiveGpuSlots = useMemo(() => {
    const inactiveSlots = new Set<number>();
    gpuNicSlotMap.forEach((config) => {
      // activeGpus is a bitmap where bit N represents GPU N+1 (1-indexed)
      // gpuSlot is 1-indexed, so we check bit (gpuSlot - 1)
      const isActive = isBitSet(activeGpus, config.gpuSlot - 1);
      if (!isActive) {
        inactiveSlots.add(config.gpuSlot);
      }
    });
    return inactiveSlots;
  }, [gpuNicSlotMap, activeGpus]);

  // Create a mapping from NIC slots to the GPU slots they serve
  // This is derived from the backend gpuNicSlotMap configuration
  const nicToGpuSlotsMap = useMemo((): ReadonlyMap<
    number,
    ReadonlySet<number>
  > => {
    const mapping = new Map<number, Set<number>>();

    for (const config of gpuNicSlotMap) {
      // Each config has a gpuSlot and an array of nicSlots that serve it
      for (const nicSlot of config.nicSlots) {
        let gpuSet = mapping.get(nicSlot);
        if (!gpuSet) {
          gpuSet = new Set();
          mapping.set(nicSlot, gpuSet);
        }
        gpuSet.add(config.gpuSlot);
      }
    }

    // The Map<number, Set<number>> is compatible with ReadonlyMap<number, ReadonlySet<number>>
    return mapping;
  }, [gpuNicSlotMap]);

  return {
    gpuSlotNumbers,
    inactiveGpuSlots,
    nicToGpuSlotsMap
  };
};

/**
 * Get the GPU slots that should be highlighted when a NIC slot is selected.
 *
 * @param nicToGpuSlotsMap - Map from NIC slot to GPU slots
 * @param selectedSlot - The currently selected NIC slot number
 * @returns Set of GPU slot numbers that should be highlighted
 */
export const getHighlightedGpuSlots = (
  nicToGpuSlotsMap: ReadonlyMap<number, ReadonlySet<number>>,
  selectedSlot: number | undefined
): ReadonlySet<number> => {
  if (selectedSlot === undefined) {
    return new Set();
  }
  return nicToGpuSlotsMap.get(selectedSlot) ?? new Set();
};
